import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Printer, FileText, Download, Globe, Check, AlertCircle, RefreshCw, TestTube, Copy, CheckCircle, X, Settings, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { PrintManager, getAvailablePrintMethods } from '@/services/printManager';
import * as printerApi from '@/services/printerApi';

const PrintManagerPage = () => {
  const navigate = useNavigate();
  
  // State
  const [activeTab, setActiveTab] = useState('quick-print');
  const [printMethod, setPrintMethod] = useState('browser');
  const [availableMethods, setAvailableMethods] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  
  // Quick Print form
  const [codes, setCodes] = useState('');
  const [copies, setCopies] = useState(1);
  const [labelType, setLabelType] = useState('usn');
  
  // Batch print
  const [batchProgress, setBatchProgress] = useState(null);
  
  // Test print
  const [testCode, setTestCode] = useState('TEST-001');
  const [testResult, setTestResult] = useState(null);
  
  useEffect(() => {
    const methods = getAvailablePrintMethods();
    setAvailableMethods(methods);
  }, []);

  const handleQuickPrint = async () => {
    const codeList = codes.split('\n').map(c => c.trim()).filter(c => c);
    
    if (codeList.length === 0) {
      toast.error('Please enter at least one code');
      return;
    }
    
    setIsLoading(true);
    try {
      const result = await PrintManager.printLabels(codeList, {
        method: printMethod,
        copies,
        labelType
      });
      
      setLastResult(result);
      toast.success(result.message || 'Print job created successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to print');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestPrint = async () => {
    setIsLoading(true);
    try {
      const result = await PrintManager.printLabels([testCode], {
        method: printMethod,
        copies: 1
      });
      
      setTestResult(result);
      toast.success('Test print created');
    } catch (error) {
      toast.error(error.message || 'Test print failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBatchPrint = async () => {
    const codeList = codes.split('\n').map(c => c.trim()).filter(c => c);
    
    if (codeList.length === 0) {
      toast.error('Please enter codes for batch printing');
      return;
    }
    
    setIsLoading(true);
    setBatchProgress({ completed: 0, total: codeList.length, percentage: 0 });
    
    try {
      const result = await PrintManager.printBatch(
        codeList,
        { method: printMethod, copies, batchSize: 50 },
        (progress) => setBatchProgress(progress)
      );
      
      setLastResult(result);
      toast.success(`Batch print complete: ${result.totalLabels} labels in ${result.batches} batches`);
    } catch (error) {
      toast.error(error.message || 'Batch print failed');
    } finally {
      setIsLoading(false);
      setBatchProgress(null);
    }
  };

  const downloadRawFile = async () => {
    const codeList = codes.split('\n').map(c => c.trim()).filter(c => c);
    
    if (codeList.length === 0) {
      toast.error('Please enter codes to download');
      return;
    }
    
    setIsLoading(true);
    try {
      const result = await PrintManager.downloadRawCommands(codeList, {
        language: 'tspl',
        copies
      });
      
      toast.success(`Downloaded ${result.filename}`);
    } catch (error) {
      toast.error(error.message || 'Download failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      {/* Header */}
      <header className="bg-[#1A1F2E] text-[#F9FAFB] sticky top-0 z-40 shadow-lg">
        <div className="max-w-6xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <Printer className="w-6 h-6 text-green-400" />
              <div>
                <h1 className="text-xl font-bold font-display">Print Manager</h1>
                <p className="text-xs text-[#E8DCC8]">JavaScript-based Label Printing</p>
              </div>
            </div>
          </div>
          <Badge variant="outline" className="bg-green-500/20 text-green-300 border-green-500/50">
            <Zap className="w-3 h-3 mr-1" />
            No WebUSB Required
          </Badge>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-6 py-8">
        {/* Info Banner */}
        <Card className="mb-6 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-green-800">Reliable Printing Without WebUSB</h3>
                <p className="text-sm text-green-700 mt-1">
                  This print manager uses browser-native printing and PDF generation - no driver issues or "Access Denied" errors.
                  Works on all browsers and operating systems.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <Card className="bg-white border border-[#E5DFD0]">
              <CardHeader>
                <CardTitle>Print Labels</CardTitle>
                <CardDescription>Enter codes to print, one per line</CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="grid grid-cols-3 mb-6">
                    <TabsTrigger value="quick-print">Quick Print</TabsTrigger>
                    <TabsTrigger value="batch-print">Batch Print</TabsTrigger>
                    <TabsTrigger value="test-print">Test Print</TabsTrigger>
                  </TabsList>

                  {/* Quick Print Tab */}
                  <TabsContent value="quick-print" className="space-y-4">
                    <div>
                      <Label className="mb-2 block">Codes (one per line)</Label>
                      <Textarea
                        value={codes}
                        onChange={(e) => setCodes(e.target.value)}
                        placeholder="USN-001&#10;USN-002&#10;USN-003"
                        className="h-40 font-mono text-sm"
                        data-testid="codes-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {codes.split('\n').filter(c => c.trim()).length} code(s) entered
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="mb-2 block">Print Method</Label>
                        <Select value={printMethod} onValueChange={setPrintMethod}>
                          <SelectTrigger data-testid="print-method-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {availableMethods.map(method => (
                              <SelectItem key={method.id} value={method.id}>
                                <div className="flex items-center gap-2">
                                  {method.id === 'browser' && <Globe className="w-4 h-4" />}
                                  {method.id === 'pdf' && <FileText className="w-4 h-4" />}
                                  {method.id === 'raw' && <Download className="w-4 h-4" />}
                                  {method.name}
                                  {method.recommended && (
                                    <Badge variant="secondary" className="ml-2 text-xs">Recommended</Badge>
                                  )}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="mb-2 block">Copies per Label</Label>
                        <Select value={copies.toString()} onValueChange={(v) => setCopies(parseInt(v))}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[1, 2, 3, 4, 5].map(n => (
                              <SelectItem key={n} value={n.toString()}>{n}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="flex gap-3 pt-4">
                      <Button
                        onClick={handleQuickPrint}
                        disabled={isLoading}
                        className="flex-1 bg-[#2D5F4F] hover:bg-[#234A3D]"
                        data-testid="print-button"
                      >
                        {isLoading ? (
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Printer className="w-4 h-4 mr-2" />
                        )}
                        Print Labels
                      </Button>
                      <Button
                        onClick={downloadRawFile}
                        disabled={isLoading}
                        variant="outline"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download .PRN
                      </Button>
                    </div>
                  </TabsContent>

                  {/* Batch Print Tab */}
                  <TabsContent value="batch-print" className="space-y-4">
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <h3 className="font-medium text-blue-800 mb-2">Batch Printing</h3>
                      <p className="text-sm text-blue-700">
                        For large print jobs (100+ labels), batch printing processes codes in groups
                        to prevent browser timeouts.
                      </p>
                    </div>

                    <div>
                      <Label className="mb-2 block">Codes (one per line)</Label>
                      <Textarea
                        value={codes}
                        onChange={(e) => setCodes(e.target.value)}
                        placeholder="Paste all codes here, one per line"
                        className="h-40 font-mono text-sm"
                      />
                    </div>

                    {batchProgress && (
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <div className="flex justify-between text-sm mb-2">
                          <span>Processing...</span>
                          <span>{batchProgress.completed} / {batchProgress.total}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-[#2D5F4F] h-2 rounded-full transition-all duration-300"
                            style={{ width: `${batchProgress.percentage}%` }}
                          />
                        </div>
                      </div>
                    )}

                    <Button
                      onClick={handleBatchPrint}
                      disabled={isLoading}
                      className="w-full bg-blue-600 hover:bg-blue-700"
                    >
                      {isLoading ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Printer className="w-4 h-4 mr-2" />
                      )}
                      Start Batch Print
                    </Button>
                  </TabsContent>

                  {/* Test Print Tab */}
                  <TabsContent value="test-print" className="space-y-4">
                    <div>
                      <Label className="mb-2 block">Test Code</Label>
                      <Input
                        value={testCode}
                        onChange={(e) => setTestCode(e.target.value)}
                        placeholder="TEST-001"
                        className="font-mono"
                        data-testid="test-code-input"
                      />
                    </div>

                    <div>
                      <Label className="mb-2 block">Print Method</Label>
                      <Select value={printMethod} onValueChange={setPrintMethod}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {availableMethods.map(method => (
                            <SelectItem key={method.id} value={method.id}>
                              {method.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <Button
                      onClick={handleTestPrint}
                      disabled={isLoading}
                      className="w-full bg-green-600 hover:bg-green-700"
                      data-testid="test-print-button"
                    >
                      {isLoading ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <TestTube className="w-4 h-4 mr-2" />
                      )}
                      Print Test Label
                    </Button>

                    {testResult && (
                      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center gap-2 text-green-700 mb-2">
                          <Check className="w-5 h-5" />
                          <span className="font-medium">Test Print Created</span>
                        </div>
                        <p className="text-sm text-green-600">{testResult.message}</p>
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Print Methods Info */}
            <Card className="bg-white border border-[#E5DFD0]">
              <CardHeader>
                <CardTitle className="text-lg">Print Methods</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2 mb-1">
                    <Globe className="w-4 h-4 text-green-600" />
                    <span className="font-medium text-green-800">Browser Print</span>
                    <Badge className="bg-green-500 text-white text-xs">Recommended</Badge>
                  </div>
                  <p className="text-xs text-green-700">
                    Opens native print dialog with formatted labels. Works on all browsers.
                  </p>
                </div>

                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-2 mb-1">
                    <FileText className="w-4 h-4 text-blue-600" />
                    <span className="font-medium text-blue-800">PDF Download</span>
                  </div>
                  <p className="text-xs text-blue-700">
                    Generates a PDF file. Print from your PDF viewer for precise control.
                  </p>
                </div>

                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-2 mb-1">
                    <Download className="w-4 h-4 text-gray-600" />
                    <span className="font-medium text-gray-800">Raw Commands</span>
                  </div>
                  <p className="text-xs text-gray-600">
                    Downloads .PRN file with TSPL commands. For advanced users with printer utilities.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Quick Tips */}
            <Card className="bg-white border border-[#E5DFD0]">
              <CardHeader>
                <CardTitle className="text-lg">Quick Tips</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-gray-600">
                <div className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Use Browser Print for quickest results</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Set your thermal printer as default for one-click printing</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>For large batches (100+), use Batch Print tab</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Test with a single label first to verify alignment</span>
                </div>
              </CardContent>
            </Card>

            {/* Last Result */}
            {lastResult && (
              <Card className="bg-white border border-[#E5DFD0]">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    Last Print Job
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Method:</span>
                      <span className="font-medium capitalize">{lastResult.method}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Labels:</span>
                      <span className="font-medium">{lastResult.labelCount || lastResult.totalLabels}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Copies:</span>
                      <span className="font-medium">{lastResult.copies || 1}</span>
                    </div>
                    {lastResult.filename && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">File:</span>
                        <span className="font-medium text-xs">{lastResult.filename}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Help Section */}
        <Card className="mt-6 bg-white border border-[#E5DFD0]">
          <CardHeader>
            <CardTitle>Troubleshooting</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
            <div>
              <h4 className="font-medium text-[#1A1F2E] mb-2">Labels not aligned?</h4>
              <ul className="text-gray-600 space-y-1">
                <li>• Ensure correct label size is set in printer settings</li>
                <li>• Use your printer's calibration function</li>
                <li>• Try the PDF method for precise sizing</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-[#1A1F2E] mb-2">QR codes not scanning?</h4>
              <ul className="text-gray-600 space-y-1">
                <li>• Increase print quality/darkness in printer settings</li>
                <li>• Clean the print head</li>
                <li>• Try a different label type</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-[#1A1F2E] mb-2">Using .PRN file?</h4>
              <ul className="text-gray-600 space-y-1">
                <li>• Windows: <code className="bg-gray-100 px-1 rounded">copy /b labels.prn \\PC\Printer</code></li>
                <li>• Or use TSC BarTender / Diagnostic Tool</li>
                <li>• Some printers accept drag-and-drop of .prn files</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-[#1A1F2E] mb-2">Still having issues?</h4>
              <ul className="text-gray-600 space-y-1">
                <li>• Try the legacy <a href="/admin/printer-setup" className="text-blue-600 hover:underline">Printer Setup Wizard</a></li>
                <li>• Check printer is connected and online</li>
                <li>• Verify label stock is loaded correctly</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default PrintManagerPage;
